/**
 * The single place that talks to the backend.
 *
 * Every call goes through `request`, so authentication, error shaping and the
 * "your session expired" path are handled once rather than in each component.
 */

const API_BASE_URL = (
  import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8080"
).replace(/\/$/, "");

/** Matches the backend's error envelope. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly field?: string;

  constructor(status: number, code: string, message: string, field?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.field = field;
  }

  /** True when the operator needs to sign in again. */
  get isAuthFailure(): boolean {
    return this.status === 401;
  }

  /** True when Cloudreve has not been authorised yet. */
  get isDisconnected(): boolean {
    return this.code === "cloudreve_not_connected";
  }
}

/** Notified whenever a request comes back 401, so the app can sign out. */
type SessionExpiredHandler = () => void;
let onSessionExpired: SessionExpiredHandler = () => {};

export function setSessionExpiredHandler(handler: SessionExpiredHandler): void {
  onSessionExpired = handler;
}

let sessionToken: string | null = null;

export function setSessionToken(token: string | null): void {
  sessionToken = token;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Sent as-is; used for uploads. Takes precedence over `body`. */
  formData?: FormData;
  signal?: AbortSignal;
  /** Skips the Authorization header, for endpoints that take no session. */
  anonymous?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};

  if (!options.anonymous && sessionToken) {
    headers.Authorization = `Bearer ${sessionToken}`;
  }
  // FormData sets its own multipart boundary, so never override it.
  if (options.body !== undefined && !options.formData) {
    headers["Content-Type"] = "application/json";
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: options.method ?? "GET",
      headers,
      body: options.formData ?? (options.body !== undefined ? JSON.stringify(options.body) : undefined),
      signal: options.signal,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new ApiError(0, "network_error", "Could not reach the server. Check that the backend is running.");
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const error = new ApiError(
      response.status,
      payload?.code ?? "unknown_error",
      payload?.message ?? `Request failed with status ${response.status}`,
      payload?.field,
    );
    if (error.isAuthFailure && !options.anonymous) {
      onSessionExpired();
    }
    throw error;
  }

  return payload as T;
}

/* -------------------------------------------------------------------------- */
/* Types mirroring the backend responses                                       */
/* -------------------------------------------------------------------------- */

export interface LoginResult {
  token: string;
  expires_at: string;
  username: string;
}

export interface ConnectionStatus {
  connected: boolean;
  account_label?: string;
  access_expires?: string;
  refresh_expires?: string;
  refresh_valid: boolean;
  updated_at?: string;
}

export interface Settings {
  base_uri: string;
  policy_id: string;
  max_size_bytes: number;
  allowed_mime: string[];
  filename_strategy: "original" | "hash" | "uuid";
  date_subfolder: boolean;
}

export interface UploadRecord {
  id: string;
  file_name: string;
  uri: string;
  url: string;
  mime_type: string;
  size_bytes: number;
  source: "api" | "web";
  api_key_id?: string;
  created_at: string;
}

export interface GalleryPage {
  items: UploadRecord[];
  total: number;
  page: number;
  page_size: number;
}

export interface GalleryStats {
  total_uploads: number;
  total_bytes: number;
  uploads_today: number;
}

export interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

export interface CreatedApiKey extends ApiKey {
  secret: string;
}

/* -------------------------------------------------------------------------- */
/* Endpoints                                                                   */
/* -------------------------------------------------------------------------- */

export const api = {
  login: (username: string, password: string) =>
    request<LoginResult>("/api/admin/login", {
      method: "POST",
      body: { username, password },
      anonymous: true,
    }),

  me: () => request<{ username: string }>("/api/admin/me"),

  connection: {
    status: () => request<ConnectionStatus>("/api/admin/cloudreve/status"),
    authorize: () =>
      request<{ authorize_url: string; state: string }>("/api/admin/cloudreve/authorize", {
        method: "POST",
      }),
    refresh: () => request<ConnectionStatus>("/api/admin/cloudreve/refresh", { method: "POST" }),
    disconnect: () => request<void>("/api/admin/cloudreve", { method: "DELETE" }),
  },

  settings: {
    get: () => request<Settings>("/api/admin/settings"),
    update: (changes: Partial<Settings>) =>
      request<Settings>("/api/admin/settings", { method: "PATCH", body: changes }),
  },

  gallery: {
    list: (page: number, pageSize: number, search: string, signal?: AbortSignal) => {
      const query = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
      });
      if (search) query.set("q", search);
      return request<GalleryPage>(`/api/admin/gallery?${query}`, { signal });
    },
    stats: () => request<GalleryStats>("/api/admin/gallery/stats"),
    remove: (id: string) => request<void>(`/api/admin/gallery/${id}`, { method: "DELETE" }),
  },

  uploads: {
    create: (file: File, signal?: AbortSignal) => {
      const formData = new FormData();
      formData.append("file", file);
      return request<UploadRecord>("/api/admin/uploads", {
        method: "POST",
        formData,
        signal,
      });
    },
    refreshLink: (id: string) =>
      request<UploadRecord>(`/api/admin/uploads/${id}/refresh-link`, { method: "POST" }),
  },

  apiKeys: {
    list: () => request<{ items: ApiKey[] }>("/api/admin/api-keys"),
    create: (name: string) =>
      request<CreatedApiKey>("/api/admin/api-keys", { method: "POST", body: { name } }),
    /** Stops the key working but keeps it listed. */
    revoke: (id: string) =>
      request<void>(`/api/admin/api-keys/${id}/revoke`, { method: "POST" }),
    /** Removes the key and its history entirely. */
    remove: (id: string) => request<void>(`/api/admin/api-keys/${id}`, { method: "DELETE" }),
  },
};

/** The Scalar-rendered API reference, served by the backend. */
export const DOCS_URL = `${API_BASE_URL}/docs`;

export { API_BASE_URL };
